import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import device from 'managers/device';
import { NodeTracker } from 'managers/nodes/tracker';
import { EffectsContainer, withEffects } from 'mixins/withEffects';
import './overlay.html';
import { IOverlayVM } from './overlay.interfaces';
import './overlay.scss';
import { IOverlayButton } from './overlayButton.interfaces';
import { ExtendedOverlayStageVMConfig, IOverlayStageVM, OverlayStageVMConfig } from './overlayStage.interfaces';

const $document = $(document);
const $html = $('html');
const $head = $('head');
const $body = $("body");

components.registerLocal('overlay-stage', () => require('components/overlay/overlayStage'));

@injectable()
export default class OverlayVM<T> implements IOverlayVM<T> {
    private element: HTMLElement

    stagesConfigs = ko.observableArray<ExtendedOverlayStageVMConfig<T>>();
    stages = ko.observableArray<IOverlayStageVM>();
    stage = ko.observable<IOverlayStageVM>();

    width: Subscribable<number>
    height: Subscribable<number>

    buttons: Subscribable<Array<IOverlayButton>>

    isActive: Subscribable<boolean>
    hasButtons: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.element = <HTMLElement>info.element;

        const { width, height } = new NodeTracker({ node: <HTMLElement>info.element });
        this.width = width;
        this.height = height;

        this.buttons = this.stage.pluck(s => s.isBusy() ? [] : s.buttons, []);

        this.isActive = this.stage.isNotNull();
        this.hasButtons = this.buttons.any();

        effects.register(stages => {
            const stage = _.last(stages);

            this.stage(stage);
            this.stages().forEach(s => s.isActive(s === stage));
        }, [this.stages]);

        effects.register(stage => {
            if (stage != undefined)
                return this.setupStage();
        }, [this.stage]);

        ko.applyBindingsToNode(info.element, {
            'click': (m: this, e: JQueryEventObject) => this.onClick(e),

            'css': {
                'wcc-overlay': true,
                'wcc-overlay--active': this.isActive
            }
        });
    }

    addStage(config: OverlayStageVMConfig<T>): Promise<IOverlayStageVM<T>> {
        return new Promise(resolve => {
            const extendedConfig = {
                ...config,

                overlay: this,

                onOpen: stage => {
                    resolve(stage);

                    if (config.onOpen)
                        config.onOpen(stage);
                }
            } as ExtendedOverlayStageVMConfig<T>;

            this.stagesConfigs.push(extendedConfig);
        });
    }

    removeStage(stage: IOverlayStageVM<T>) {
        this.stagesConfigs.remove(stage.config);
    }

    clear() {
        this.stagesConfigs.removeAll();
    }

    private setupStage() {
        const effects = withEffects();

        const initialBodyWidth = $body.width() ?? 0;
        const scrollPos = $document.scrollTop() ?? 0;

        effects.register(() => {
            const effects = [];

            $html.addClass('overlay-enabled');
            effects.push(() => $html.removeClass('overlay-enabled'));

            const offset = ($body.width() ?? 0) - initialBodyWidth;

            if (offset > 1) {
                const styleNode = $('<style type="text/css">.overlay-enabled body { margin-right: ' + offset + 'px; }</style>');

                $head.append(styleNode);
                effects.push(() => styleNode.remove());
            }

            return effects;
        });

        effects.register(deviceHeight => {
            $body.css({ 'height': `${deviceHeight}px` });

            return () => $body.css({ 'height': '' });
        }, [device.height]);

        effects.register(() => {
            $body.scrollTop(scrollPos);

            return () => $document.scrollTop(scrollPos)
        });

        return effects;
    }

    private onClick(e: JQueryEventObject) {
        var stage = this.stage();

        if (stage != undefined && !stage.isLocked && (e.target === this.element || $(e.target).is('.wcc-overlay__backdrop')))
            stage.close();

        return true;
    }
}